import { ColDef } from '@ag-grid-community/core';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { GetPluginDetailsQuery } from '@uigy/generated/graphql';
import { AssetType, FindingFragment, FindingStatus } from '@uigy/generated/graphql';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import {
  KpiDrawerInput,
  PillsInput,
} from '@uigy/shared/components/ui-drawer/components/ui-drawer-content-header/interface';
import {
  PillTypes,
} from '@uigy/shared/components/ui-drawer/components/ui-drawer-content-header/ui-drawer-content-header.enums';
import { StatusPillType } from '@uigy/shared/components/ui-status-pill/ui-status-pill-typed/ui-status-pill-typed.enums';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { LanguageService } from '@uigy/shared/services/language.service';
import { PluginsService } from '@uigy/modules/plugins/services/plugins.service';
import { DetailsGridSettingsService } from '@uigy/modules/plugins/services/details-grid-settings.service';
import {
  VulnerabilityFindingsGridSettingsService,
} from '@uigy/modules/findings/components/findings-vulnerabilities/services/vulnerability-findings-grid-settings.service';
import {
  VulnerabilityFindingsService,
} from '@uigy/modules/findings/components/findings-vulnerabilities/services/vulnerability-findings.service';
import { DataChipIcons, DataChipInput } from '@uigy/shared/components/data-chip/interface';
import { SubSectionType } from '@uigy/shared/components/ui-drawer/components/ui-drawer-section/constants';
import { DrawerSubSection } from '@uigy/shared/components/ui-drawer/components/ui-drawer-section/interface';
import { FindingsDrawerQueryParams } from '@uigy/shared/components/ui-drawer/drawer.enums';
import {
  SeverityCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/severity-cell-renderer/severity-cell-renderer.component';
import {
  VprCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/vpr-cell-renderer/vpr-cell-renderer.component';
import { IemService } from '@uigy/shared/services/iem.service';
import { SideDrawerUrlParametersService } from '@uigy/shared/services/url-params/side-drawer-url-parameters.service';
import { VprService } from '@uigy/shared/services/vpr.service';
import Utils from '@uigy/utils';
import { NoResultsFoundOverlay } from '@uigy/shared/components/ui-grid/models/overlays';


@Injectable({
  providedIn: 'root',
})
export class VulnerabilityFindingsSideDrawerService {
  private languageService = inject(LanguageService);
  private pluginsService = inject(PluginsService);
  private detailsGridSettingsService = inject(DetailsGridSettingsService);
  private findingsGridSettingsService = inject(VulnerabilityFindingsGridSettingsService);
  private findingsService = inject(VulnerabilityFindingsService);
  private route = inject(ActivatedRoute);
  private iemService = inject(IemService);
  private sideDrawerUrlParametersService = inject(SideDrawerUrlParametersService);
  private drawerHandle?: import('@uigy/shared/services/url-params/side-drawer-url-parameters.service').RegisteredDrawerHandle;


  public readonly findingsTexts = this.languageService.texts.findings;
  public selectedFinding = signal<FindingFragment | undefined>(undefined);
  public readonly output = computed(() => this.selectedFinding()?.output?.trim() ?? '');

  private plugin?: GetPluginDetailsQuery['plugins']['nodes'][number];
  public isLoading = signal<boolean>(true);
  public isEmpty = signal<boolean>(false);
  public isDrawerOpen = signal<boolean>(false);
  public noResultsOverlay = signal<NoResultsFoundOverlay | undefined>(undefined);

  public constructor() {
    // Register generic drawer handling (auto-inits from current URL)
    this.drawerHandle = this.sideDrawerUrlParametersService.registerDrawer({
      paramKey: FindingsDrawerQueryParams.SelectedFindingId,
      getCurrentId: () => this.selectedFinding()?.id,
      isOpen: () => this.isDrawerOpen(),
      onSelect: (id, source) => {
        this.isDrawerOpen.set(true);
        if (id !== this.selectedFinding()?.id) {
          this.loadFinding(id);
        }
      },
      onClear: () => {
        this.selectedFinding.set(undefined);
        this.isDrawerOpen.set(false);
      },
      openHistoryMode: 'push',
      closeHistoryMode: 'push'
    });
  }

  private loadFinding(findingId: string): void {
    this.isLoading.set(true);
    this.isEmpty.set(false);
    this.findingsService.getFindingById(findingId).subscribe((finding) => {
      if (!finding) {
        this.isEmpty.set(true);
      }
      this.selectedFinding.set(finding);
      this.pluginsService.getPluginDetails(this.selectedFinding()?.plugin?.id ?? 0).subscribe({
        next: (result) => {
          this.plugin = result;
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error fetching plugin details:', err);
          this.isEmpty.set(true);
          this.noResultsOverlay.set({
            title: this.languageService.texts.general.no_results.title,
            items: [{text: this.languageService.texts.general.no_results.subtitle}],
          });
        },
      });
    });
  }

  public setSelectedFinding(findingId: string | undefined, options?: { skipUrlUpdate?: boolean }): void {
    if (findingId) {
      if (options?.skipUrlUpdate) {
        // URL already reflects the state
        this.isDrawerOpen.set(true);
        if (findingId !== this.selectedFinding()?.id) {
          this.loadFinding(findingId);
        }
      } else {
        this.drawerHandle?.open(findingId, { historyMode: 'push' });
      }
    } else {
      this.closeDrawer(options);
    }
  }

  public closeDrawer(options?: { skipUrlUpdate?: boolean }): void {
    if (options?.skipUrlUpdate) {
      this.selectedFinding.set(undefined);
      this.isDrawerOpen.set(false);
    } else {
      this.drawerHandle?.close({ historyMode: 'push' });
    }
  }

  public outputSubSections(): DrawerSubSection[] {
    const text = this.output();
    return [{
      subSectionProps: {
        subSectionType: SubSectionType.longText,
        subSectionContent: {
          text: text,
          isCodeBlock: true,
        },
      },
    }];
  }

  public vulnerabilitySubSections(): DrawerSubSection[] {
    const overviewGridSettings = this.detailsGridSettingsService.OVERVIEW_GRID_SETTINGS;
    overviewGridSettings.components = { ...overviewGridSettings.components, 'severityCellRenderer': SeverityCellRendererComponent };
    overviewGridSettings?.columnDefs?.forEach((columnDef: ColDef) => {
      if (columnDef.field === 'details.severity') {
        columnDef.cellRenderer = 'severityCellRenderer';
      }
    });

    const vprGridSettings = this.detailsGridSettingsService.VPR_GRID_SETTINGS;
    vprGridSettings.components = {
      ...vprGridSettings.components,
      'vprCellRenderer': VprCellRendererComponent,
      'severityCellRenderer': SeverityCellRendererComponent,
    };
    vprGridSettings?.columnDefs?.forEach((columnDef: ColDef) => {
      if (columnDef.field === 'vprScore') {
        columnDef.cellRenderer = 'vprCellRenderer';
      }
    });

    const categoryRiskGridSettings = this.detailsGridSettingsService.RISK_GRID_SETTINGS;
    categoryRiskGridSettings.components = { ...categoryRiskGridSettings.components, 'vprCellRenderer': VprCellRendererComponent };
    categoryRiskGridSettings?.columnDefs?.forEach((columnDef: ColDef) => {

      if (['details.cvssBaseScore', 'details.cvssTemporalScore', 'details.cvssV3BaseScore', 'details.cvssV3TemporalScore'].includes(columnDef.field as string)) {
        columnDef.cellRenderer = 'vprCellRenderer';
      } else if (columnDef.field === 'details.stigSeverity') {
        columnDef.cellRenderer = 'severityCellRenderer';
      }
    });

    return [{
      title: this.languageService.texts.plugins.details.category_overview,
      subSectionProps: {
        subSectionType: SubSectionType.keyValueGrid,
        subSectionContent: {
          data: this.plugin ?? {},
          gridOptionsOnly: true,
          removeEmptyFields: true,
          hideEmptyTable: true,
          gridOptions: overviewGridSettings,
        },
      },
    },
      {
        title: this.languageService.texts.plugins.details.category_vpr_key_drivers,
        subSectionProps: {
          subSectionType: SubSectionType.keyValueGrid,
          subSectionContent: {
            data: this.plugin ?? {},
            gridOptionsOnly: true,
            removeEmptyFields: true,
            hideEmptyTable: true,
            gridOptions: vprGridSettings,
          },
        },
      },
      {
        title: this.languageService.texts.plugins.details.category_refs,
        subSectionProps: {
          subSectionType: SubSectionType.keyValueGrid,
          subSectionContent: {
            data: this.plugin ?? {},
            gridOptionsOnly: true,
            removeEmptyFields: true,
            hideEmptyTable: true,
            gridOptions: this.detailsGridSettingsService.REF_GRID_SETTINGS,
          },
        },
      },
      {
        title: this.languageService.texts.plugins.details.category_risk_info,
        subSectionProps: {
          subSectionType: SubSectionType.keyValueGrid,
          subSectionContent: {
            data: this.plugin ?? {},
            gridOptionsOnly: true,
            removeEmptyFields: true,
            hideEmptyTable: true,
            gridOptions: categoryRiskGridSettings,
          },
        },
      },
      {
        title: this.languageService.texts.plugins.details.category_exploitability_information,
        subSectionProps: {
          subSectionType: SubSectionType.keyValueGrid,
          subSectionContent: {
            data: this.plugin ?? {},
            gridOptionsOnly: true,
            removeEmptyFields: true,
            hideEmptyTable: true,
            gridOptions: this.detailsGridSettingsService.EXPLOITABILITY_GRID_SETTINGS,
          },
        },
      },
      {
        title: this.languageService.texts.plugins.details.category_plugin_details,
        subSectionProps: {
          subSectionType: SubSectionType.keyValueGrid,
          subSectionContent: {
            data: this.plugin ?? {},
            gridOptionsOnly: true,
            removeEmptyFields: true,
            hideEmptyTable: true,
            gridOptions: this.detailsGridSettingsService.PLUGIN_DETAILS_GRID_SETTINGS,
          },
        },
      },
    ];
  }

  public affectedAssetSubSections(): DrawerSubSection[] {

    return [{
      subSectionProps: {
        subSectionType: SubSectionType.keyValueGrid,
        subSectionContent: {
          data: this.selectedFinding() ?? {},
          gridOptionsOnly: true,
          removeEmptyFields: true,
          hideEmptyTable: true,
          gridOptions: this.findingsGridSettingsService.SIDE_DRAWER_AFFECTED_ASSET_GRID_SETTINGS,
        },
      },
    },
    ];
  }

  public headerTopSectionDataChip(): DataChipInput {
    return {
      label: this.findingsTexts.vulnerability_findings.side_drawer.vulnerability,
      leftIcons: [{ generalIcon: IconsEnum.vulnerability }],
    };
  }

  public headerBottomSectionDataChipsTop(): DataChipInput[] {
    let leftIcons: DataChipIcons[] = [{ assetType: this.selectedFinding()?.asset?.type || AssetType.Unknown }];
    if (this.selectedFinding()?.asset?.vendor) {
      leftIcons.push({ vendorName: this.selectedFinding()?.asset?.vendor as string });
    }

    const dataChipInputs: DataChipInput[] = [
      {
        links: [{
          displayName: this.selectedFinding()?.asset?.name || '',
          path: this.assetExternalURL(),
          isExternal: true,
        }],
        leftIcons: leftIcons,
      }
    ];
    if (this.selectedFinding()?.port !== undefined) {
      let value: string = this.selectedFinding()?.port.toString() || '';
      if (this.selectedFinding()?.protocol) {
        value = `${value}/${this.selectedFinding()?.protocol?.toUpperCase() || ''}`;
      }

      dataChipInputs.push({
        label: this.findingsTexts.vulnerability_findings.grid.port,
        values: [value],
      });
    }
    return dataChipInputs;
  };

  public headerPills(): PillsInput[] {
    const pills: PillsInput[] = [];
    const severity = this.selectedFinding()?.plugin?.severity;
    if (severity) {
      pills.push(
        { pillType: PillTypes.severity, pillInput: severity },
      );
    }
    const status = this.selectedFinding()?.status;
    if (status) {
      pills.push(
        { pillType: PillTypes.status, pillInput: { pillType: StatusPillType.findings, pillStatus: status } },
      );
      if (status === FindingStatus.Resolved) {
        pills.push(
          { pillType: PillTypes.status, pillInput: { pillType: StatusPillType.findings, pillStatus: 'Fixed' } },
        );
      }
    }
    return pills;
  }

  public headerBottomSectionDataChipsBottom(): DataChipInput[] {
    const dataChips: DataChipInput[] = [
      { label: this.findingsTexts.vulnerability_findings.grid.plugin_source, values: [this.selectedFinding()?.plugin?.source || ''] },
      { label: this.findingsTexts.vulnerability_findings.grid.plugin_id, values: [this.selectedFinding()?.plugin?.id?.toString() || ''] },
    ];

    if (this.selectedFinding()?.status === FindingStatus.Resolved) {
      dataChips.push(
        {
          label: this.findingsTexts.vulnerability_findings.grid.fixed_at,
          values: [DateTimeService.utcToLocalFormatted(this.selectedFinding()?.fixedAt, DatesFormatsEnum.fullWithDot) || ''],
        },
      );
    } else {
      dataChips.push(
        {
          label: this.findingsTexts.vulnerability_findings.grid.last_hit,
          values: [DateTimeService.utcToLocalFormatted(this.selectedFinding()?.lastHit, DatesFormatsEnum.fullWithDot) || ''],
        },
      );
    }

    return dataChips;
  }

  public lastHitString(): string {
    return DateTimeService.utcToLocalFormatted(this.selectedFinding()?.lastHit, DatesFormatsEnum.fullWithDot);
  }

  public assetExternalURL(): string {
    return `${this.iemService.isIcpViewInEm() ? Utils.getBaseLocation() : RoutesStatesNamesEnum.root}/${RoutesStatesNamesEnum.singleAssetPage}?id=${this.selectedFinding()?.asset?.id}`;
  }

  public headerBottomSectionKpiDrawers(): KpiDrawerInput[] {
    const kpisTexts = this.languageService.texts.findings.vulnerability_findings.side_drawer.header.kpis;
    const kpis: KpiDrawerInput[] = [];
    const vprTypeProgressBarSubValue = '/10';

    // VPR KPI - vprScore is on the plugin itself, not in details
    const vprScore = this.plugin?.vprScore ?? undefined ;

    const vprOption = VprService.getVPRScoreOption(vprScore);
    kpis.push({
      label: kpisTexts.vpr.label,
      value: vprScore,
      subValue: vprTypeProgressBarSubValue,
      icon: IconsEnum.VPR,
      infoTooltip: kpisTexts.vpr.tooltip,
      progressBarType: 'vpr',
      progressBarValue: vprScore,
      vprScore: vprOption,
    });


    // CVSSv3 KPI - cvssV3BaseScore is in plugin details
    const cvssV3BaseScore = this.plugin?.details?.cvssV3BaseScore;
    const cvssScore = cvssV3BaseScore ? parseFloat(cvssV3BaseScore) : undefined;
    kpis.push({
      label: kpisTexts.cvss3.label,
      value: cvssScore,
      subValue: vprTypeProgressBarSubValue,
      icon: IconsEnum.vulnerability,
      progressBarType: 'vpr',
      progressBarValue: cvssScore,
      vprScore: VprService.getVPRScoreOption(cvssScore),
    });


    // Criticality KPI - from the affected asset
    const assetCriticality = this.selectedFinding()?.asset?.criticality;
    kpis.push({
      label: kpisTexts.criticality.label,
      value: this.languageService.formatEnum('Criticality', assetCriticality),
      icon: IconsEnum.acr,
      progressBarType: 'criticality',
      criticality: assetCriticality,
    });


    return kpis;
  }
}
